import * as pdfjsLib from 'pdfjs-dist';

console.log('PDF.js import result:', pdfjsLib);
console.log('PDF.js keys:', pdfjsLib ? Object.keys(pdfjsLib) : 'null');

// Try multiple approaches to set the worker
const setWorker = () => {
  const workerUrl = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
  
  // Method 1: Direct GlobalWorkerOptions
  if (pdfjsLib && pdfjsLib.GlobalWorkerOptions) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
    console.log('Worker set via pdfjsLib.GlobalWorkerOptions');
    return;
  }
  
  // Method 2: Default export GlobalWorkerOptions
  if (pdfjsLib && pdfjsLib.default && pdfjsLib.default.GlobalWorkerOptions) {
    pdfjsLib.default.GlobalWorkerOptions.workerSrc = workerUrl;
    console.log('Worker set via pdfjsLib.default.GlobalWorkerOptions');
    return;
  }
  
  // Method 3: Try window global (fallback)
  if (typeof window !== 'undefined' && window.pdfjsLib && window.pdfjsLib.GlobalWorkerOptions) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
    console.log('Worker set via window.pdfjsLib.GlobalWorkerOptions');
    return;
  }
  
  console.warn('Could not set PDF.js worker - GlobalWorkerOptions not found');
};

// Set worker with error handling
try {
  setWorker();
} catch (error) {
  console.error('Error setting PDF.js worker:', error);
}

export const initializePDFViewer = async () => {
  if (!pdfData?.pdfUrl) {
    console.error('PDF URL not available');
    return;
  }

  const container = document.getElementById("pdf-viewer-app");
  if (!container) {
    console.error('PDF viewer container not found');
    return;
  }

  try {
    console.log('Loading PDF:', pdfData.pdfUrl);
    
    // Create PDF viewer instance
    const viewer = new SimplePDFViewer(container, pdfData.pdfUrl);
    await viewer.initialize();
    
  } catch (error) {
    console.error('Error initializing PDF viewer:', error);
    container.innerHTML = `<div style="text-align: center; padding: 40px; color: #666;">
      Error loading PDF: ${error.message}
    </div>`;
  }
}

class SimplePDFViewer {
  constructor(container, pdfUrl) {
    this.container = container;
    this.pdfUrl = pdfUrl;
    this.pdf = null;
    this.currentPage = 1;
    this.totalPages = 0;
    this.canvas = null;
    this.context = null;
    
    // Get navigation elements
    this.prevBtn = document.querySelector('.cco-app-controls .button-prev');
    this.nextBtn = document.querySelector('.cco-app-controls .button-next');
    this.pageInfo = document.querySelector('.cco-app-controls .pagination');
  }

  // Helper method to get PDF.js functions with fallback handling
  getPDFJSFunction(functionName) {
    // Try direct access
    if (pdfjsLib && pdfjsLib[functionName]) {
      console.log(`Found ${functionName} via direct access`);
      return pdfjsLib[functionName];
    }
    
    // Try default export
    if (pdfjsLib && pdfjsLib.default && pdfjsLib.default[functionName]) {
      console.log(`Found ${functionName} via default export`);
      return pdfjsLib.default[functionName];
    }
    
    // Try window global
    if (typeof window !== 'undefined' && window.pdfjsLib && window.pdfjsLib[functionName]) {
      console.log(`Found ${functionName} via window global`);
      return window.pdfjsLib[functionName];
    }
    
    console.error(`PDF.js function '${functionName}' not found anywhere`);
    return null;
  }

  async initialize() {
    // Setup canvas
    this.container.innerHTML = `
      <canvas id="pdf-canvas" style="
        width: 100%; 
        height: auto; 
        display: block; 
        max-width: 100%;
        background: transparent;
        padding: 0;
        margin: 0;
      "></canvas>
    `;
    
    this.canvas = document.getElementById('pdf-canvas');
    this.context = this.canvas.getContext('2d');

    // Get PDF.js getDocument function with fallback handling
    const getDocumentFunc = this.getPDFJSFunction('getDocument');
    if (!getDocumentFunc) {
      throw new Error('PDF.js getDocument method not available. Library not loaded properly.');
    }
    
    this.pdf = await getDocumentFunc(this.pdfUrl).promise;
    this.totalPages = this.pdf.numPages;
    
    console.log(`PDF loaded: ${this.totalPages} pages`);

    // Setup navigation
    this.setupNavigation();
    
    // Render first page
    await this.renderPage(1);
  }

  async renderPage(pageNumber) {
    if (!this.pdf || pageNumber < 1 || pageNumber > this.totalPages) {
      return;
    }

    try {
      const page = await this.pdf.getPage(pageNumber);
      
      // Calculate scale to fit container
      const containerWidth = this.container.offsetWidth - 40;
      const viewport = page.getViewport({ scale: 1.0 });
      const scale = Math.min(containerWidth / viewport.width, 1.5);
      const scaledViewport = page.getViewport({ scale: scale });

      // Set canvas size
      this.canvas.height = scaledViewport.height;
      this.canvas.width = scaledViewport.width;

      // Render the page
      const renderContext = {
        canvasContext: this.context,
        viewport: scaledViewport
      };

      await page.render(renderContext).promise;
      
      this.currentPage = pageNumber;
      this.updateNavigation();
      
      console.log(`Rendered page ${pageNumber}/${this.totalPages}`);
      
    } catch (error) {
      console.error('Error rendering page:', error);
    }
  }

  setupNavigation() {
    // Previous button
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        if (this.currentPage > 1) {
          this.renderPage(this.currentPage - 1);
        }
      });
    }

    // Next button
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        if (this.currentPage < this.totalPages) {
          this.renderPage(this.currentPage + 1);
        }
      });
    }

    // Setup pagination bullet click handlers
    this.setupPaginationClickHandlers();

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.target.tagName.toLowerCase() === 'input') return; // Don't interfere with form inputs
      
      if (e.key === 'ArrowLeft' && this.currentPage > 1) {
        e.preventDefault();
        this.renderPage(this.currentPage - 1);
      } else if (e.key === 'ArrowRight' && this.currentPage < this.totalPages) {
        e.preventDefault();
        this.renderPage(this.currentPage + 1);
      }
    });

    // Handle resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.renderPage(this.currentPage);
      }, 250);
    });
  }

  updateNavigation() {
    // Hide entire controls if only one page
    const controlsContainer = document.querySelector('.cco-app-controls');
    if (controlsContainer) {
      if (this.totalPages <= 1) {
        controlsContainer.style.display = 'none';
        return; // Skip the rest of the navigation setup
      } else {
        controlsContainer.style.display = 'block';
      }
    }

    // Update page info with clickable pagination bullets
    if (this.pageInfo) {
      this.pageInfo.innerHTML = this.generatePaginationBullets();
    }

    // Update previous button
    if (this.prevBtn) {
      if (this.currentPage <= 1) {
        this.prevBtn.style.opacity = '0.5';
        this.prevBtn.style.cursor = 'not-allowed';
        this.prevBtn.disabled = true;
      } else {
        this.prevBtn.style.opacity = '1';
        this.prevBtn.style.cursor = 'pointer';
        this.prevBtn.disabled = false;
      }
    }

    // Update next button
    if (this.nextBtn) {
      if (this.currentPage >= this.totalPages) {
        this.nextBtn.style.opacity = '0.5';
        this.nextBtn.style.cursor = 'not-allowed';
        this.nextBtn.disabled = true;
      } else {
        this.nextBtn.style.opacity = '1';
        this.nextBtn.style.cursor = 'pointer';
        this.nextBtn.disabled = false;
      }
    }
  }

  generatePaginationBullets() {
    if (this.totalPages <= 1) return '';
    
    let bullets = [];
    
    // For many pages, show a smart pagination
    if (this.totalPages <= 10) {
      // Show all pages if 10 or fewer
      for (let i = 1; i <= this.totalPages; i++) {
        bullets.push(this.createBullet(i, i === this.currentPage));
      }
    } else {
      // Smart pagination for many pages
      bullets.push(this.createBullet(1, this.currentPage === 1));
      
      if (this.currentPage > 4) {
        bullets.push('<span class="pagination-ellipsis">...</span>');
      }
      
      // Show pages around current page
      const start = Math.max(2, this.currentPage - 1);
      const end = Math.min(this.totalPages - 1, this.currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== this.totalPages) {
          bullets.push(this.createBullet(i, i === this.currentPage));
        }
      }
      
      if (this.currentPage < this.totalPages - 3) {
        bullets.push('<span class="pagination-ellipsis">...</span>');
      }
      
      if (this.totalPages > 1) {
        bullets.push(this.createBullet(this.totalPages, this.currentPage === this.totalPages));
      }
    }
    
    return bullets.join('');
  }

  createBullet(pageNumber, isActive) {
    const activeClass = isActive ? ' pagination-bullet-active' : '';
    return `<span class="pagination-bullet${activeClass}" data-page="${pageNumber}">${pageNumber}</span>`;
  }

  setupPaginationClickHandlers() {
    if (!this.pageInfo) return;
    
    this.pageInfo.addEventListener('click', (e) => {
      if (e.target.classList.contains('pagination-bullet')) {
        const pageNumber = parseInt(e.target.dataset.page);
        if (pageNumber && pageNumber !== this.currentPage) {
          this.renderPage(pageNumber);
        }
      }
    });
  }
}
